import axiosNest from '../../api/axiosNest';
import * as featureFlagsApi from '../featureFlags/featureFlagsApi';
import { normalizeRoleAccess, setRoleAccess, type RoleAccess } from './roleAccessStore';

/**
 * Odczyt uprawnień i widoczności zalogowanego z centrum ról (decyzja
 * użytkownika 22.10.2026, przełącznik `roleCenter`, S-22.10-01):
 * `GET /role-center/me`, raz na sesję, i tylko przy włączonym przełączniku —
 * wyłączony (wersja „obecna”): trasy nie ma (404), więc w ogóle nie pytamy.
 *
 * Woła ją `authProvider.getPermissions()`. Rola systemowa trafia do ekranów
 * od razu (pierwszy widok z lustra w kodzie = zasiew centrum), a stan
 * centrum przelicza menu i sekcje, gdy przyjdzie (`useRoleAccessState`);
 * czeka na nią tylko rola własna z centrum (poprawka 23.10.2026 — wcześniej
 * do czasu odczytu menu wyglądało jak u gościa). Odczyt się nie udał =
 * `null` (lustro w kodzie, jak dotąd) i bez zapamiętania — kolejne wejście
 * spyta jeszcze raz.
 */

/** Klucz przełącznika — dane z API (`feature-flags.ts`), nie tłumaczymy. */
export const ROLE_CENTER_FLAG = 'roleCenter';

/** Limit czasu — rola wstrzymuje ekrany, więc zawieszone zapytanie nie może ich trzymać. */
export const ROLE_ACCESS_TIMEOUT_MS = 10_000;

let pending: Promise<RoleAccess | null> | null = null;
let followsFlags = false;

/**
 * Zmiana przełączników albo wersji konta (menu „Wersja”) — stan centrum od
 * nowa: w wersji „obecnej” centrum nie działa i obowiązuje lustro w kodzie.
 */
function followFlags(): void {
  if (followsFlags || typeof featureFlagsApi.subscribeEffectiveFlags !== 'function') return;
  followsFlags = true;
  featureFlagsApi.subscribeEffectiveFlags(() => {
    pending = null;
    void loadRoleAccess();
  });
}

export function loadRoleAccess(): Promise<RoleAccess | null> {
  followFlags();
  if (!pending) {
    pending = (async () => {
      const flags = await featureFlagsApi.loadEffectiveFlags();
      if (flags?.flags[ROLE_CENTER_FLAG] !== true) {
        setRoleAccess(null);
        // Przełączniki mogą jeszcze nie przyjść (błąd) — spytamy ponownie.
        if (!flags) pending = null;
        return null;
      }
      try {
        const res = await axiosNest.get('/role-center/me', { timeout: ROLE_ACCESS_TIMEOUT_MS });
        const access = normalizeRoleAccess(res?.data);
        setRoleAccess(access);
        return access;
      } catch {
        pending = null;
        setRoleAccess(null);
        return null;
      }
    })();
  }
  return pending;
}

/** Odczyt od nowa (po zmianie przełącznika albo wersji). */
export async function reloadRoleAccess(): Promise<RoleAccess | null> {
  pending = null;
  return loadRoleAccess();
}

/** Zapomnij stan poprzedniego konta (logowanie, wylogowanie). */
export function forgetRoleAccess(): void {
  pending = null;
  setRoleAccess(null);
}

/** Tylko dla testów: zapomnij odczyt i obserwację przełączników. */
export function resetRoleAccessForTests(): void {
  pending = null;
  followsFlags = false;
  setRoleAccess(null);
}
